import {getIframeVideo, scrollTo, isMatchMedia} from '%helpers%/utils';

class Video {
  constructor(element) {
    if (!element) return;

    this.element = element;
    this.modal = null;
    this.modalEl = null;
    this.videoBlock =
      (this._getTarget() && document.querySelector(this._getTarget())) ||
      this.element.closest('.video-wrapper');
    this.playlist = this.element.closest('.widget-video_list');

    if (!this.element.classList.contains('active')) {
      this._shouldPlayInside() ? this._playInside() : this._showVideoModal();
      this._setActivePlaylistItem();
    }
  }

  _showVideoModal() {
    this._showModal();
    this._setListeners();
  }

  _showModal() {
    this.closeAllInlineVideos();

    const template = this._getModalNode();
    document.body.appendChild(template);

    this.modalEl = document.querySelector('.modal-video');
    this.modal = bootstrap.Modal.getOrCreateInstance(this.modalEl, {
      keyboard: true,
      backdrop: true,
    });

    const videoPreview = this.element.closest('.video-wrapper, .card-img-block');

    if (!videoPreview) {
      if (this._isLocalVideo()) this._createLocalVideo();
      this.modal.show(this.element);
      return;
    }

    const img = this.element.parentNode.querySelector('.img-block');

    if (!img) {
      this.modal.show(this.element);
      return;
    }

    this.modalEl.classList.add('invisible');

    const rect = img.getBoundingClientRect();
    const clone = img.cloneNode(true);
    clone.classList.add('clone-preview');

    const top = rect.top + (rect.height - img.offsetHeight) / 2;
    const left = rect.left + (rect.width - img.offsetWidth) / 2;

    const duration = isMatchMedia('md') ? 1300 : 600;

    Object.assign(clone.style, {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${img.offsetWidth}px`,
      height: `${img.offsetHeight}px`,
      borderRadius: '10px',
      transition: `all ${duration}ms cubic-bezier(0.33, 0, 0.67, 1)`,
    });

    document.body.appendChild(clone);
    this.modal.show(this.element);

    requestAnimationFrame(() => {
      const videoBlock = this.modalEl.querySelector('.video-block');
      const targetRect = videoBlock.getBoundingClientRect();

      Object.assign(clone.style, {
        top: `${targetRect.top}px`,
        left: `${targetRect.left}px`,
        width: `${targetRect.width}px`,
        height: `${targetRect.height}px`,
        transform: 'none',
        borderRadius: this._getRatio() ? `${targetRect.borderRadius}px` : 0,
      });

      setTimeout(() => {
        if (this._isLocalVideo()) this._createLocalVideo();
        this.modalEl.classList.remove('invisible');
        setTimeout(() => clone.remove(), 150);
      }, duration);
    });
  }

  _setListeners() {
    this.modalEl.addEventListener('hidden.bs.modal', () => {
      this._stopAllVideos(this.modalEl);

      this.modal.dispose();
      this.modalEl.remove();

      this.modal = null;
      this.modalEl = null;
      this.videoBlock = null;
    });
  }

  _getSrc() {
    return this.element.dataset.src;
  }

  _getIframeVideo() {
    return getIframeVideo(this._getSrc(), {autoplay: 1, mute: 1, fullscreen: 1, modestbranding: 0, rel: 0});
  }

  _getTarget() {
    return this.element.dataset.bsTarget || null;
  }

  _getRatio() {
    return this.element.dataset.ratio || null;
  }

  _getImgPreview() {
    return this.element.dataset.imgSrc || null;
  }

  _isLocalVideo() {
    const src = this._getSrc();
    if (!src) return false;

    if (this.element.dataset.localVideo === 'true') return true;

    const normalizedSrc = src.split('?')[0].toLowerCase();
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(normalizedSrc);
  }

  _shouldPlayInside() {
    const {videoInside = null, bsToggle} = this.element.dataset;
    return (videoInside && videoInside === 'true') || bsToggle === 'video';
  }

  _getModalNode() {
    const template = document.createElement('div');
    const ratioClassName = this._getRatio() ? 'ratio-' + this._getRatio() : 'h-100';

    template.classList.add('modal', 'modal-video');
    template.classList.toggle('is-fullscreen', ratioClassName === 'h-100');
    template.setAttribute('tabindex', '-1');
    template.setAttribute('aria-hidden', 'true');
    template.innerHTML = `
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            <div class="modal-dialog ${ratioClassName === 'h-100' ? 'modal-fullscreen' : 'modal-dialog-centered modal-xl'}">
               <div class="modal-content">
                    <div class="modal-body p-0">
                        <div class="video-block show ratio ${ratioClassName} bg-black"></div>
                    </div>
                </div>
            </div>`;

    this.videoBlock = template.querySelector('.video-block');

    //if (this._isLocalVideo()) this._createLocalVideo(); moved to _showModal after animation completed
    if (!this._isLocalVideo()) this._createIframeVideo();

    return template;
  }

  _playInside() {
    if (!this.videoBlock) return;

    this.closeAllInlineVideos();
    this.videoBlock.classList.add('show');
    this.videoBlock.closest('.corner-cut')?.classList.add('is-playing-video');

    const video = this.videoBlock.querySelector('.img-block');

    if (this._isLocalVideo()) {
      const videoElement = document.createElement('video');
      videoElement.classList = 'video';
      videoElement.setAttribute('controls', 'controls');
      videoElement.setAttribute('autoplay', 'autoplay');
      videoElement.innerHTML = `<source src="${this._getSrc()}" type="video/mp4">Your browser does not support the video tag.`;
      video.appendChild(videoElement);
    } else if (this._shouldPlayInside() && !this._isLocalVideo()) {
      video.appendChild(this._getIframeVideo());
    }

    if (!this.playlist) {
      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.classList.add('btn-close', 'btn-close-video');
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.addEventListener('click', () => this._closeInlineVideo());
      this.videoBlock.appendChild(closeButton);
    }
  }

  _closeInlineVideo(block) {
    const videoBlock = block || this.videoBlock;
    if (!videoBlock) return;

    videoBlock.classList.remove('show');
    this.videoBlock.closest('.corner-cut')?.classList.remove('is-playing-video');

    this._stopAllVideos(videoBlock);
    videoBlock.querySelector('.btn-close-video')?.remove();
  }

  _createLocalVideo() {
    const videoElement = document.createElement('video');
    videoElement.setAttribute('controls', 'controls');
    videoElement.setAttribute('autoplay', 'autoplay');
    videoElement.innerHTML = `<source src="${this._getSrc()}" type="video/mp4">Your browser does not support the video tag.`;
    this.videoBlock.appendChild(videoElement);
  }

  _createIframeVideo() {
    const iframe = this._getIframeVideo();
    iframe.addEventListener('load', () => {
      iframe.classList.add('loaded');
    });
    this.videoBlock.appendChild(iframe);
  }

  _stopAllVideos(container) {
    if (!container) return;

    container.querySelectorAll('iframe, video').forEach(el => {
      if (el.tagName === 'VIDEO') {
        el.pause();
        el.removeAttribute('src');
        el.load();
        el.remove();
        return;
      }

      if (el.tagName === 'IFRAME') {
        el.src = '';
        el.remove();
      }
    });
  }

  _setActivePlaylistItem() {
    if (!this.playlist) return;

    const active = this.playlist.querySelector('.list-group-item.active');
    active?.classList.remove('active');

    this.element.classList.add('active');

    if (!isMatchMedia('desktop')) {
      const offsetTop = this.videoBlock.offsetTop;
      const headerHeight = document.querySelector('.page-header')?.offsetHeight || 0;

      scrollTo(offsetTop - headerHeight - 16);
    }
  }

  closeAllInlineVideos() {
    document.querySelectorAll('.video-wrapper.show').forEach(videoBlock => {
      this._closeInlineVideo(videoBlock);
    });
  }
}

export default Video;
